import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import he from 'he';

// Redux Store
import { writerAnalyzerSelector } from 'store/Writer/Analyser';
import { UserSessionSelector } from 'store/UserSession';
import { writerOrderDisplaySelector } from 'store/Writer/OrderDisplay';
import {
  fetchWriterOrderDisplay,
  fetchWriterTextOrderDisplay,
} from 'store/Writer/OrderDisplay/features';
import { fetchAllOrders } from 'store/Writer/OrderList/features';
import {
  writerRedactionDataSelector,
  clearRedactionData,
} from 'store/Writer/RedactionData';

// Components
import { Form } from '@unform/web';
import { Loader } from 'components/shared/Loader';
import ComparisonModal from 'components/shared/modal/ComparisonModal';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import ErrorSuccesModal from 'components/shared/modal/ErrorSuccesModal';
import SavingIndicator from 'components/shared/AutoSaveDisplay';
import { CustomEditor } from 'components/shared/CustomEditor';
import { DownloadOneTextWordType } from 'components/shared/DownloadFile/DownloadTextWordType';
import { RevisionDetails } from 'components/shared/RevisionDetails';
import KeywordStuffingModal from 'components/shared/modal/KeywordStuffingModals/KeywordStuffingModal';
import { TextDisplaySidebar } from 'components/shared/TextDisplaySidebar/TextDisplaySidebar';
import { InitialTextSection } from 'components/shared/InitialTextSection';
import AnalyseText from 'components/Admin/AnalyseText';
import { FormHeader } from 'components/shared/TextDisplay/FormHeader';
import { TextDetailsSection } from 'components/shared/TextDisplay/TextDetailsSection';
import { LanguageDropdown } from 'components/shared/TextDisplay/LanguageDropdown';
import { WordCountDisplay } from 'components/shared/TextDisplay/WordCountDisplay';

// Helpers
import { checkData } from 'helpers/formHelper';
import {
  extractStrKeywordsWithFrequency,
  transformToSemanticGap,
  extractKeywordsWithFrequency,
} from 'helpers/FormatKeywordsHelper';
import { autoSaveText } from 'helpers/CreateAutoSave';

// Utils
import {
  ANALYSELANGAUGESLIST,
  GOVERNED_WRITERS_WITH_VALIDATION,
  KeywordStuffingModalPrompts,
} from 'utils/Constants';
import { extractWords } from 'utils/keywordStuffing/keywordStuffing';
import { keywordsAutoGeneration } from 'utils/KeywordsAutoGeneration/keywordsAutoGeneration';

// Hooks
import { useAutosave } from 'utils/hooks/useAutoSave';
import { useSidebar } from 'utils/hooks/features/textDisplay/useSidebar';
import { useWriterTextForm } from 'utils/hooks/features/textDisplay/writer/useWriterTextForm';
import { useEditorManagement } from 'utils/hooks/features/textDisplay/useEditorManagement';
import { useWordCountTracker } from 'utils/hooks/features/textDisplay/useWordCountTracker';
import { useTextStatus } from 'utils/hooks/features/textDisplay/useTextStatus';
import { useWriterTextSave } from 'utils/hooks/features/textDisplay/writer/useWriterTextSave';
import { useWriterPutToDraft } from 'utils/hooks/features/textDisplay/writer/useWriterPutToDraft';
import { useWriterSendRevision } from 'utils/hooks/features/textDisplay/writer/useWriterSendRevision';
import { useWriterDeliverText } from 'utils/hooks/features/textDisplay/writer/useWriterDeliverText';
import { useWriterKeywordStuffing } from 'utils/hooks/features/textDisplay/writer/useWriterKeywordStuffing';
import { useRequestIncreaseAnalysis } from 'utils/hooks/features/textDisplay/writer/useRequestIncreaseAnalysis';
import { useWriterFormValidation } from 'utils/hooks/features/textDisplay/writer/useWriterFormValidation';
import { replaceTextInBold, entitiesToSpecialChars } from 'utils/StringUtils';

import {
  useWordsInBold,
  WordsProvider,
} from 'utils/contexts/ScoreBoldWordsContext';

import {
  SemanticScoreProvider,
  useSemanticScore,
} from 'utils/contexts/SemanticScoreContext';

function WriterCreateTextContent() {
  const RED = '#EE3900';
  const ORANGE = '#FF8D4B';
  const BLACK = '#3c3c3c';

  const [language, setLanguage] = useState(ANALYSELANGAUGESLIST[0]);
  const [analyseCount, setAnalyseCount] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);
  const [isSendRevisionDisabled, setIsSendRevisionDisabled] = useState(false);

  const [confirmation, setConfirmation] = useState(false);
  const [confirmationMsg, setConfirmationMsg] = useState('');

  const [showComparison, setShowComparison] = useState(false);

  const [errorSuccess, setErrorSuccess] = useState(false);
  const [errorSuccessMsg, setErrorSuccessMsg] = useState('');
  const [errorSuccessImg, setErrorSuccessImg] = useState(undefined);
  const [isSuccess, setIsSuccess] = useState(false);

  const [color, setColor] = useState(BLACK);
  const [allWebsitesArePresent, setAllWebsitesArePresent] = useState(false);
  const [autoKeywordsGenerationLoading, setAutokeywordGenerationLoading] =
    useState(false);
  const [validTextNbr, setValidTextNbr] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [metaDescriptionError, setMetaDescriptionError] = useState(false);

  const { t } = useTranslation();

  const history = useHistory();

  const dispatch = useDispatch();

  const { user } = useSelector(UserSessionSelector);
  const {
    formData,
    isLoading,
    isError,
    orderId,
    orderType,
    orderContentType,
    orderSiloName,
    textType,
    orderWebsite,
  } = useSelector(writerAnalyzerSelector);

  const redactionData = useSelector(writerRedactionDataSelector);
  const { wordsInBold } = useWordsInBold();
  const { semanticScore } = useSemanticScore();

 
  const [currentContentScore, setCurrentContentScore] = useState(null);

  const { minWords, maxWords, order } = useSelector(writerOrderDisplaySelector);
  const { limit, analysis } = useSelector(UserSessionSelector);

  const formRef = useRef(null);

  const { scheduleSave, isSaving } = useAutosave(autoSaveText, 1000);

  const {
    sidebarWidth,
    setSidebarWidth,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarRef,
    handleOpenSidebarAndScrollTop,
    showSidebar,
    handleScrollBottom,
  } = useSidebar(textType,orderContentType);

  const {
    // State values
    textTitle,
    keyword,
    url,
    titleTag,
    metaDescriptionTag,
    actualTextContent,
    actualHtmlContent,
    analyseText,
    analyseResult,
    keywords,
    combinedKeywords,
    list,
    stuffedKeywords,
    websites,
    initialTextContent,
    initialHtmlContent,
    // Setters
    setTextTitle,
    setKeyword,
    setUrl,
    setTitleTag,
    setMetaDescriptionTag,
    setActualTextContent,
    setActualHtmlContent,
    setAnalyseText,
    setAnalyseResult,
    setKeywords,
    setCombinedKeywords,
    setCombinedKeywordsList,
    setList,
    setStuffedKeywords,
    setWebsites,
    setInitialTextContent,
    setInitialHtmlContent,
  } = useWriterTextForm(formData, orderType, isLoading);

  const {
    editorRef,
    stepOneEditorRef,
    editorInitialized,
    stepOneEditorInitialized,
    handleEditorInit,
    handleStepOneEditorInit,
  } = useEditorManagement();

  const {
    wordCount: initialTextWordCount,
    setWordCount: setInitialTextWordCount,
    getWordCount,
  } = useWordCountTracker(stepOneEditorRef, stepOneEditorInitialized);

  const {
    wordCount: actualTextWordCount,
    debouncedSetWordCount: debouncedSetActualTextWordCount,
  } = useWordCountTracker(editorRef, editorInitialized);

  const {
    isPremium,
    hasInitialTextContent,
    isNotTranslation,
    isDelivered,
    isRevisionDone,
    isRevisionSent,
    isDraft,
    isEmpty,
  } = useTextStatus(formData, orderType, user.role, textType);

  const { deliverText, isTextDelivered, setIsTextDelivered, orderTextsList } =
    useWriterDeliverText({
      t,
      textType,
      combinedKeywords,
      setErrorSuccessMsg,
      setIsSuccess,
      setErrorSuccess,
      titleTag,
      metaDescriptionTag,
      setIsFetchLoading,
      actualHtmlContent,
      actualTextContent,
      semanticScore,
      initialHtmlContent,
      initialTextContent,
      keyword,
      textTitle,
      actualTextWordCount,
      initialTextWordCount,
      url,
      websites,
      setErrorSuccessImg,
      orderId,
      formData,
      orderType,
      orderContentType,
    });

  const {
    keywordStuffingLoading,
    initialStuffedKeywords,
    showKeywordStuffingModal,
    keywordStuffingModalTitle,
    keywordStuffingModalMsg,
    keywordStuffingModalData,
    keywordStuffingModalCloseBtn,
    //keywordStuffingCall,
    setShowKeywordStuffingModal,
    setInitialStuffedKeywords,
  } = useWriterKeywordStuffing({
    setSidebarCollapsed,
    handleScrollBottom,
    actualHtmlContent,
    setActualHtmlContent,
    textTitle,
    keywords,
    extractWords,
    textType,
    initialTextContent,
    actualTextContent,
    initialTextWordCount,
    actualTextWordCount,
    setStuffedKeywords,
    deliverText,
    t,
    orderType,
    setActualTextContent,
    setInitialHtmlContent,
  });

  const { handleSave, isTextFieldDisabled } = useWriterTextSave({
    formData,
    initialHtmlContent,
    initialTextContent,
    actualTextContent,
    actualTextWordCount,
    initialTextWordCount,
    combinedKeywords,
    keyword,
    metaDescriptionTag,
    textTitle,
    titleTag,
    url,
    textType,
    stuffedKeywords,
    websites,
    initialStuffedKeywords,
    analyseResult,
    orderId,
    setConfirmation,
    setActualTextContent,
    setActualHtmlContent,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setIsSuccess,
    setErrorSuccess,
    t,
  });

  const { putToDraft } = useWriterPutToDraft({
    textType,
    combinedKeywords,
    url,
    initialTextContent,
    orderType,
    orderContentType,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    initialHtmlContent,
    currentContentScore,
    keyword,
    metaDescriptionTag,
    textTitle,
    titleTag,
    actualTextWordCount,
    initialTextWordCount,
    stuffedKeywords,
    websites,
    initialStuffedKeywords,
    formData,
    orderId,
    t,
    setShowKeywordStuffingModal,
    isEmpty,
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
    setIsFetchLoading,
    setErrorSuccessImg,
  });

  const { sendRevision } = useWriterSendRevision({
    t,
    actualHtmlContent,
    actualTextContent,
    analyseResult,
    keyword,
    metaDescriptionTag,
    combinedKeywords,
    setIsFetchLoading,
    setErrorSuccessMsg,
    setErrorSuccessImg,
    setErrorSuccess,
    formData,
    setIsSuccess,
  });

  const { requestIncreaseAnalysis, showCreditButton, setShowCreditButton } =
    useRequestIncreaseAnalysis({
      t,
      setErrorSuccessMsg,
      setIsSuccess,
      setErrorSuccess,
    });

  const enableSave = useCallback(() => {
    if (
      (formData && formData.url && !formData.activeInitialText) ||
      isEmpty(initialTextContent) ||
      isEmpty(url)
    ) {
      setIsSaveDisabled(true);
      return;
    }
    setIsSaveDisabled(false);
    return;
  }, [formData, initialTextContent, url]);

  function handleKeywordChange(e) {
    setCombinedKeywords(e.target.value);
    setKeywords(extractStrKeywordsWithFrequency(e.target.value));
  }

  const { checkValidForm, disabledForm } = useWriterFormValidation({
    actualTextContent,
    actualTextWordCount,
    analyseResult,
    formData,
    initialTextWordCount,
    keyword,
    metaDescriptionTag,
    minWords,
    orderType,
    textTitle,
    textType,
    titleTag,
    validTextNbr,
    semanticScore,
    orderContentType,
  });

  const enableSendRevision = useCallback(() => {
    if (formData && formData.revisionDone) {
      setIsSendRevisionDisabled(true);
      return;
    }
    setIsSendRevisionDisabled(false);
    return;
  }, [formData]);

  const revisionKeywordStuffing = () => {
    let demandeRevisionKeywordStuffing = 'keywordStuffingDraft';
    putToDraft(demandeRevisionKeywordStuffing);
  };

 
  async function validateData(data, { reset }, event) {
    let action = event.nativeEvent.submitter.value;

  
    if (action === 'PutToDraft') {
      const isTitleEmpty = !titleTag || !titleTag.trim();
      const isMetaDescriptionEmpty = !metaDescriptionTag || !metaDescriptionTag.trim();
      
      if (isTitleEmpty || isMetaDescriptionEmpty) {
   
        setTitleError(isTitleEmpty);
        setMetaDescriptionError(isMetaDescriptionEmpty);
        
        
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return; // Stop submission, no popup
      } else {
   
        setTitleError(false);
        setMetaDescriptionError(false);
      }
    }

   
    let finalHtmlContent = actualHtmlContent;  

 
    if (action === 'PutToDraft' || action === 'DeliverText') {
      finalHtmlContent = await applyHighlightingAndWait();
    }
 
    const wrappedPutToDraft = (demande) =>
      putToDraft(demande, finalHtmlContent);
    const wrappedDeliverText = (demande) =>
      deliverText(demande, finalHtmlContent);
  
    await checkData(
      data,
      orderType,
      orderContentType,
      formRef,
      action,
      wrappedPutToDraft,
      wrappedDeliverText,
       // () => Promise.resolve(true),
        // keywordStuffingCall,
      setConfirmationMsg,
      setConfirmation
    );
  }

  function resetFields() {
    setTextTitle('');
    setKeyword('');
    setUrl('');
    setTitleTag('');
    setMetaDescriptionTag('');
    // setActiveInitialText("")
    setActualTextContent('');
    setActualHtmlContent('');
    setInitialTextContent('');
    setInitialHtmlContent('');
    setAnalyseResult('');
    setKeywords('');
    setCombinedKeywords('');
    setWebsites('');
    setInitialStuffedKeywords([]);
    setStuffedKeywords([]);
    setList({});
    setIsTextDelivered(false);
  }

  const handleInputChange = (e) => {
    setWebsites(e.target.value);
    checkIfAllLinesGreen(actualHtmlContent, e.target.value);
  };

  const checkIfAllLinesGreen = (isGreen) => {
    setAllWebsitesArePresent(isGreen);
  };

  useEffect(() => {
    document.title = t('Typix - Détails du texte');
    window.dispatchEvent(new Event('locationchange'));
  }, [t]);

  useEffect(() => {
    if (redactionData.isDataAvailable) {

      // Pre-fill keyword field
      if (redactionData.keyword && !keyword) {
        setKeyword(redactionData.keyword);
     
        if (!textTitle) {
          setTextTitle(redactionData.keyword);
        }
      }

 
      if (redactionData.keywords && !keywords) {
  
        setKeywords(redactionData.keywords);
        setCombinedKeywords(redactionData.keywords);
        setCombinedKeywordsList(transformToSemanticGap(redactionData.keywords));
      }

      if (redactionData.semanticGap && Object.keys(list).length === 0) {
        setList(redactionData.semanticGap);
      }
      if (redactionData.url && !url) {
        setUrl(redactionData.url);
      }
 
      if (redactionData.internalLinking && !websites) {
        setWebsites(redactionData.internalLinking);
      }

     
      dispatch(clearRedactionData());
    }
  }, [
    redactionData,
    keyword,
    keywords,
    list,
    url,
    combinedKeywords,
    websites,
    textTitle,
    setKeyword,
    setCombinedKeywords,
    setKeywords,
    setWebsites,
    setList,
    setTextTitle,
    setErrorSuccessMsg,
    setIsSuccess,
    setErrorSuccess,
    dispatch,
    t,
  ]);

  useEffect(() => {
    if (!formData) return;
    checkIfAllLinesGreen(formData.actualHtmlContent, formData.internalLinking);
  }, [formData, isLoading]);

  useEffect(() => {
    setAnalyseCount(limit - analysis);
    if (!analyseCount) return;
    if (analyseCount >= 0 && analyseCount <= 5) {
      setColor(RED);

      setShowCreditButton(true);

      setErrorSuccessMsg(
        t(
          "Information dernière minute ! Ton stock de crédits sera bientôt épuisé :( Pour anticiper, n'hésite pas à demander dès maintenant plus de crédits pour terminer ta commande."
        )
      );
      setIsSuccess(false);
      setErrorSuccess(true);
      return;
    }

    if (analyseCount >= 6 && analyseCount <= 10) {
      setColor(ORANGE);
      return;
    }
  }, [limit, analysis, analyseCount, t]);

  useEffect(
    () => enableSave(),
    [formData, url, initialTextContent, enableSave]
  );

  useEffect(
    () => checkValidForm(),
    [
      checkValidForm,
      formData,
      textTitle,
      keyword,
      keywords,
      titleTag,
      url,
      metaDescriptionTag,
      actualTextContent,
      analyseResult,
      validTextNbr,
    ]
  );

  useEffect(() => enableSendRevision(), [enableSendRevision, formData]);

   
  useEffect(() => {
    if (titleError && titleTag && titleTag.trim()) {
      setTitleError(false);
    }
  }, [titleTag, titleError]);

  useEffect(() => {
    if (metaDescriptionError && metaDescriptionTag && metaDescriptionTag.trim()) {
      setMetaDescriptionError(false);
    }
  }, [metaDescriptionTag, metaDescriptionError]);

 
  const highlightWordsInHtml = (htmlContent, wordsToHighlight) => {
    if (!wordsToHighlight || wordsToHighlight.length === 0) {
      return htmlContent;
    }

    let highlightedContent = htmlContent;

    let processedContent = '';
    if (orderType === 'PREMIUM') {
      if (/<\/?[a-z][\s\S]*>/i.test(analyseText)) {
        processedContent = entitiesToSpecialChars(htmlContent);
      } else {
        processedContent = he.decode(htmlContent);
      }
    } else {
      processedContent = he.decode(htmlContent);
    }

    let filteredKeywords = wordsToHighlight.filter((keyword) => {
      return keyword.length > 0 && keyword !== '';
    });

    highlightedContent = replaceTextInBold(processedContent, filteredKeywords);

    return highlightedContent;
  };

  

  const applyHighlightingAndWait = () => {
    return new Promise((resolve) => {
      if (!editorInitialized || !editorRef.current) {
     
        resolve(actualHtmlContent);
        return;
      }

      const editor = editorRef.current;
      const currentRawHtml = editor.getContent();
      const newHighlightedHtml = highlightWordsInHtml(
        currentRawHtml,
        wordsInBold
      );

      if (newHighlightedHtml !== currentRawHtml) {
        editor.setContent(newHighlightedHtml);

    
        setActualHtmlContent(() => {
        
          resolve(newHighlightedHtml);
          return newHighlightedHtml; 
        });

        
        setActualTextContent(editor.getContent({ format: 'text' }));
      } else {
    
        resolve(actualHtmlContent);
      }
    });
  };

  if (isFetchLoading || isLoading || isError) return <Loader />;

  return (
    <div className="content-center">
      <Form
        ref={formRef}
        onSubmit={validateData}
        style={{
          transition: 'margin-right 0.3s ease',
          marginRight: sidebarCollapsed || !showSidebar ? 0 : sidebarWidth,
        }}
      >
        <FormHeader
          textTitle={formData?.textTitle || t('Nouveau texte')}
          onBackClick={() => {
            dispatch(fetchWriterOrderDisplay(orderId));
            dispatch(fetchWriterTextOrderDisplay(orderId));
            dispatch(fetchAllOrders());
            history.goBack();
          }}
          onDownloadClick={() => DownloadOneTextWordType(formData.id, user)}
        />

        <TextDetailsSection
          t={t}
          userRole={user.role}
          formData={{
            textTitle,
            keyword,
            url,
            titleTag,
            metaDescriptionTag,
          }}
          handlers={{
            setTextTitle,
            setKeyword,
            setUrl,
            setTitleTag,
            setMetaDescriptionTag,
          }}
          errors={{
            titleError,
            metaDescriptionError,
          }}
        />

        {(formData?.globalRevisionSent || formData?.revisionSent) &&
          formData?.revisionText && (
            <RevisionDetails
              t={t}
              textId={formData.id}
              revisionRequestAt={formData?.revisionRequestAt}
              revisionText={formData?.revisionText}
              revisionFileName={formData?.revisionFileName}
            />
          )}

        {(isPremium || hasInitialTextContent || (!!url && url.trim() !== '')) ? (
          <div className="row mt-2">
            <InitialTextSection
              isTextFieldDisabled={isTextFieldDisabled}
              formData={formData}
              textType={textType}
              url={url}
              initialHtmlContent={initialHtmlContent}
              handleStepOneEditorInit={handleStepOneEditorInit}
              setInitialHtmlContent={setInitialHtmlContent}
              setInitialTextContent={setInitialTextContent}
              setInitialTextWordCount={setInitialTextWordCount}
              userRole={user.role}
              t={t}
              isNotTranslation={isNotTranslation}
              isSaveDisabled={isSaveDisabled}
            />
          </div>
        ) : null}

        <div className="row mt-2">
          <div className="col-12">
            <div>
              <div className="label-title label-text-h">
                <strong>{t('Étape')} 2</strong>-{' '}
                {t('Ajouter ou compléter ci-dessous votre contenu')} :
              </div>

              <div className="box-wrapper-editor">
                <CustomEditor
                  isReadOnly={
                    !(isDelivered || isRevisionDone || formData?.validated)
                  }
                  textId={formData?.id}
                  content={
                    orderType === 'SMART_WITH_REVIEW'
                      ? actualTextContent
                      : actualHtmlContent
                  }
                  onInit={(evt, editor) => handleEditorInit(editor)}
                  onChange={(value, editor) => {
                    let textValue = editor.getContent({ format: 'text' });

                    const newWordCount = editor.plugins.wordcount.getCount();
                    debouncedSetActualTextWordCount(newWordCount);

                    if (isRevisionSent || isDraft) {
                      scheduleSave(
                        textValue,
                        value,
                        formData,
                        initialHtmlContent,
                        initialTextContent,
                        orderType
                      );
                    }

                    if (orderType !== 'PREMIUM') {
                      setActualHtmlContent(value);
                      setActualTextContent(value);
                      setAnalyseText(textValue);
                    } else {
                      setActualHtmlContent(value);
                      setActualTextContent(textValue);
                      //setAnalyseText(textValue);
                      checkIfAllLinesGreen(value, websites);
                    }
                  }}
                  t={t}
                  orderType={orderType}
                />
              </div>
            </div>

            <SavingIndicator isSaving={isSaving} />

            <div className="d-flex justify-content-between flex-wrap mt-3">
              <div className="box-botom-flex">
                <div style={{ paddingLeft: 10 }}>
                  <div>
                    <LanguageDropdown
                      t={t}
                      language={language}
                      setLanguage={setLanguage}
                    />
                  </div>
                </div>
              </div>

              <div>
                {!isLoading ? (
                  <WordCountDisplay
                    userRole={user.role}
                    textType={textType}
                    t={t}
                    minWords={minWords}
                    maxWords={maxWords}
                    actualTextWordCount={getWordCount(editorRef)}
                    initialTextWordCount={getWordCount(stepOneEditorRef)}
                    formData={formData}
                    initialTextContent={initialTextContent}
                    setValidTextNbr={setValidTextNbr}
                  />
                ) : (
                  ''
                )}

                <div className="d-flex">
                  {formData &&
                  (formData.revisionSent || formData.globalRevisionSent) ? (
                    <div className="d-inline-flex">
                      <span className="me-2">
                        {isSendRevisionDisabled ? (
                          <button className="bt-submit-disabled-y">
                            {t('Enregistrer et informer client')}
                          </button>
                        ) : (
                          <button
                            className="bt-submit-y"
                            onClick={() => sendRevision()}
                          >
                            {t('Enregistrer et informer client')}
                          </button>
                        )}
                      </span>
                    </div>
                  ) : (
                    <div className="d-flex">
                      {' '}
                      {GOVERNED_WRITERS_WITH_VALIDATION.includes(
                        user?.mail
                      ) && (
                        <div style={{ flex: 1 }}>
                          {formData &&
                          formData.delivered &&
                          !formData.validated ? (
                            <button
                              className="bt-submit-y me-2"
                              style={{ background: '#43B929', color: 'white' }}
                              type="submit"
                              name="action"
                              value={'ValidateText'}
                            >
                              {t('Valider le texte')}
                            </button>
                          ) : (
                            <>
                              <button className="bt-submit-disabled-y me-2">
                                {t('Valider le texte')}
                              </button>
                            </>
                          )}
                        </div>
                      )}
                      <div style={{ flex: 1 }}>
                        {disabledForm || !allWebsitesArePresent ? (
                          <button className="bt-submit-disabled-y me-2">
                            {t('Bon pour livraison')}
                          </button>
                        ) : (
                          <button
                            className="bt-submit-y me-2"
                            style={{ background: '#43B929', color: 'white' }}
                            type="submit"
                            name="action"
                            value='DeliverText'
                          >
                            {t('Bon pour livraison')}
                          </button>
                        )}
                      </div>
                      <div>
                        <div className="d-inline-flex">
                          {textType !== 'TRANSLATED' && (
                            <>
                              {!formData ||
                              (formData &&
                                !isEmpty(formData.url) &&
                                !isEmpty(formData.initialTextContent)) ? (
                                <span className="me-2">
                                  <button
                                    className="bt-submit-y"
                                    onClick={() => {
                                      setShowComparison(true);
                                    }}
                                  >
                                    {t('Comparer')}
                                  </button>{' '}
                                </span>
                              ) : (
                                ''
                              )}
                            </>
                          )}

                          <span>
                            {(formData && formData.delivered) ||
                            (!initialTextWordCount &&
                              textType === 'TRANSLATED') ||
                            (!actualTextWordCount || actualTextWordCount === 0) ? (
                              <button
                                className="bt-submit-disabled-y"
                                type="submit"
                              >
                                {t('Brouillon')}
                              </button>
                            ) : (
                              <button
                                className="bt-submit-y"
                                type="submit"
                                name="action"
                                value="PutToDraft"
                                disabled={isFetchLoading || !actualTextWordCount}
                              >
                                {t('Brouillon')}
                              </button>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>

      {showSidebar && (
        <TextDisplaySidebar
     
          ref={sidebarRef}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          width={sidebarWidth}
          setWidth={setSidebarWidth}
        
          combinedKeywords={combinedKeywords}
          autoKeywordsGenerationLoading={autoKeywordsGenerationLoading}
          keywords={keywords}
          textType={textType}
        
          orderWebsite={orderWebsite}
          orderContentType={orderContentType}
      
          websites={websites}
       
          actualHtmlContent={actualHtmlContent}
          actualTextContent={actualTextContent}
          isDelivered={formData?.delivered}
          userRole={user.role}
          t={t}
          keywordStuffingLoading={keywordStuffingLoading}
          keyword={keyword}
          
          list={list}
          language={language.id}
          maxWords={maxWords}
          wordCount={actualTextWordCount}
         
          initialContentScore={redactionData.initialScoreData}
     
          textId={formData?.id}
        
          onScoreDataChange={setCurrentContentScore}
     
          handleInputChange={handleInputChange}
          handleKeywordChange={handleKeywordChange}
          checkIfAllLinesGreen={checkIfAllLinesGreen}
          onGenerateKeywords={() => {
            keywordsAutoGeneration(
              orderId,
              keyword,
              orderSiloName,
              url,
              setKeywords,
              setCombinedKeywords,
              setCombinedKeywordsList,
              setWebsites,
              setErrorSuccessMsg,
              setIsSuccess,
              setErrorSuccess,
              t,
              setAutokeywordGenerationLoading
            );
          }}
        />
      )}

      <div className="row"></div>
      <ComparisonModal
        open={showComparison}
        initialText={initialTextContent}
        text={he.decode(actualTextContent)}
        handleClose={() => setShowComparison(false)}
      />
      <ConfirmationModal
        open={confirmation}
        message={confirmationMsg}
        handleClose={() => setConfirmation(false)}
        executeAction={handleSave}
      />
      <KeywordStuffingModal
        open={showKeywordStuffingModal}
        title={keywordStuffingModalTitle}
        message={keywordStuffingModalMsg}
        stuffedKeywords={keywordStuffingModalData}
        showButtonClose={keywordStuffingModalCloseBtn}
        handleClose={() => setShowKeywordStuffingModal(false)}
        executeAction={revisionKeywordStuffing}
      ></KeywordStuffingModal>

      <ErrorSuccesModal
        open={errorSuccess}
        message={errorSuccessMsg}
        handleClose={() => {
          setErrorSuccess(false);
          if (
            errorSuccessMsg ===
            KeywordStuffingModalPrompts.demandeRevisionKeywordStuffing
          ) {
            dispatch(fetchWriterOrderDisplay(orderId));
            dispatch(fetchWriterTextOrderDisplay(orderId));
            dispatch(fetchAllOrders());
            history.goBack();
          }
        }}
        success={isSuccess}
        isTextDelivered={isTextDelivered}
        isAllTextesDelivered={
          orderTextsList.length === 0
            ? false
            : orderTextsList.every((text) => text.delivered)
        }
        remainingTextAmount={order && order.textAmount - orderTextsList.length}
        //orderTextsList={orderTextsList}
        analyseCount={analyseCount}
        resetFields={resetFields}
        orderId={orderId}
        orderType={orderType}
        showCreditButton={showCreditButton}
        executeAction={requestIncreaseAnalysis}
        toggle={() => setErrorSuccess(!errorSuccess)}
        user={user}
        textType={order?.textType}
        imageUrl={errorSuccessImg}
      />
    </div>
  );
}

function WriterCreateText() {
  return (
    <WordsProvider>
      <SemanticScoreProvider>
        <WriterCreateTextContent />
      </SemanticScoreProvider>
    </WordsProvider>
  );
}

export default WriterCreateText;
